/**
 * Setup step: share the WHOLE screen.
 *
 * `getDisplayMedia` lets the student pick a tab, a window, or the entire screen.
 * Only the entire screen is acceptable for proctoring, so the pick is inspected
 * (`displaySurface`) and anything less is rejected with a clear instruction to
 * try again. `Start` does not unlock until a monitor-level share is live.
 */
import { AlertTriangle, Check, Monitor } from "lucide-react";
import { Button, Card, CardBody, CardHeader } from "@/components/ui";
import type { ScreenShareHandle } from "@/lib/recorder";

export function ScreenShareStep({
  screen,
  done,
  onConfirm,
  onNotFull,
}: {
  screen: ScreenShareHandle;
  done: boolean;
  onConfirm: () => void;
  /** Called when the student shared a tab/window instead of the whole screen. */
  onNotFull: () => void;
}) {
  const ready = screen.state === "ready";

  const share = async () => {
    const result = await screen.request();
    if (result === "unfull") onNotFull();
  };

  return (
    <Card>
      <CardHeader
        title="Share your screen"
        description="Your whole screen is recorded for the exam, alongside both cameras."
      />
      <CardBody>
        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="relative shrink-0 overflow-hidden rounded-md border border-border bg-surface-sunken">
            <video
              ref={screen.videoRef}
              muted
              playsInline
              className="block h-[180px] w-[240px] object-contain"
            />
            {!ready ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <Monitor className="size-8 text-ink-faint" />
              </div>
            ) : null}
          </div>

          <div className="min-w-0 flex-1">
            {ready ? (
              <>
                <p className="flex items-center gap-1.5 text-xs text-ink">
                  <Check className="size-3.5 text-answered" />
                  Your entire screen is being shared.
                </p>
                <ul className="mt-2 list-inside list-disc text-2xs text-ink-muted">
                  <li>Leave the share running — do not stop it during the exam.</li>
                  <li>If you stop it, you will be asked to share again.</li>
                </ul>
              </>
            ) : (
              <>
                <p className="text-xs font-medium text-ink">
                  When your browser asks, choose{" "}
                  <span className="font-semibold">Entire Screen</span> — not a
                  tab and not a window.
                </p>
                <ul className="mt-2 flex flex-col gap-1 text-2xs text-ink-muted">
                  <li>· Click “Share my screen” below.</li>
                  <li>· In the picker, select your whole screen / monitor.</li>
                  <li>· Then click Share.</li>
                </ul>

                {screen.state === "unfull" ? (
                  <p className="mt-3 flex items-start gap-1.5 rounded-md border border-violation/30 bg-violation-soft px-3 py-2 text-xs text-violation">
                    <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
                    You must share your ENTIRE screen, not a tab or a window. Try
                    again and pick the whole screen.
                  </p>
                ) : screen.state === "denied" ? (
                  <p className="mt-3 rounded-md border border-review/30 bg-review-soft px-3 py-2 text-xs text-ink">
                    Screen sharing was cancelled. It is required — please share
                    your whole screen to continue.
                  </p>
                ) : screen.state === "error" ? (
                  <p className="mt-3 rounded-md border border-violation/30 bg-violation-soft px-3 py-2 text-xs text-violation">
                    This browser could not start screen sharing. Use a
                    desktop browser (Chrome) to take the exam.
                  </p>
                ) : null}

                <Button
                  className="mt-3"
                  size="sm"
                  variant="secondary"
                  icon={<Monitor />}
                  onClick={() => void share()}
                  disabled={screen.state === "requesting"}
                >
                  {screen.state === "requesting"
                    ? "Waiting for the picker…"
                    : screen.state === "idle"
                      ? "Share my screen"
                      : "Share my screen again"}
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2 border-t border-border pt-3">
          {!ready ? (
            <span className="text-2xs text-ink-muted">
              Your whole screen must be shared to continue.
            </span>
          ) : null}
          <Button
            className="ml-auto"
            variant="primary"
            disabled={!ready}
            onClick={onConfirm}
          >
            {done ? "Continue" : "My whole screen is shared"}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}
